import React, { useState, useEffect } from "react";
import { Field } from "formik";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { pink } from "@mui/material/colors";
import {
  VIPSDataServiceURL,
  requestOptions,
} from "../../Constants/GlobalConstant";

const TableELCSelectField = ({
  element,
  schemaPageId,
  schemaResult,
  schemaPageFields,
  values,
  itemId,
  tableName,
  tdkeys,
  name,
  id,
  error,
  errorBorder,
  resetELCList,
  ELLValue,
  setFieldValue,
  parentvalue,
  rowkeys,
  handleResetELCList,
  fetchParentELCHeaderData,
  searchParams,
  setOptionsForChild,
  parentELCValue,
  childOptions,
  setParentELCValue,
  ELCDropDown,
  setELCDropDown,
  loadingStates,
  tableELLValue,
  schemaItem,
  ELLSchemaItem,
  disabled,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // We extract only the specific values this component cares about so it ignores the rest of the form.
  const parentPageId = parseInt(element.parentId?.split("|")?.[0]);
  const parentId = parseInt(element.parentId?.split("|")?.[1]);

  let parentValueForDependency = null;
  if (parentPageId === schemaPageId) {
    const pSchema = schemaResult?.find((x) => x.schemaItem === parentId);
    parentValueForDependency = pSchema ? values?.[pSchema.fieldName] : null;
  } else if (parentPageId === itemId) {
    const pSchema = schemaPageFields?.find((x) => x.schemaItem === parentId);
    if (pSchema) {
      parentValueForDependency = values?.[tableName]?.[tdkeys]?.["ELC" + pSchema.fieldName] || values?.[tableName]?.[tdkeys]?.[pSchema.fieldName];
    }
  }

  const currentValueForDependency = values?.[tableName]?.[tdkeys]?.[name];
  // -------------------------------------

  useEffect(() => {
    if (element.hasChildList && parentValueForDependency) {
      let schema = {};
      let dropDownValue = "";
      if (parentPageId === schemaPageId && element.lists) {
        schema = schemaResult?.find((x) => x.schemaItem === parentId);
        dropDownValue = values?.[schema?.fieldName];
        dropDownValue = schema?.options.find(
          (x) => x.label === dropDownValue
        )?.value;

        const option = element.hasChildList
          ? options.find(
              (x) => x.label === values?.[tableName]?.[tdkeys]?.[name]
            )
          : values?.[tableName]?.[tdkeys]?.[name];

        if (ELLSchemaItem !== parentId && option?.value) {
          setLoading(true);
          fetchParentELCHeaderData(
            element,
            tdkeys,
            option?.value,
            ELCDropDown,
            tdkeys
          ).finally(() => {
            setLoading(false);
            handleResetELCList(true);
          });
          return;
        }
      } else if (parentPageId === itemId && element.lists) {
        schema = schemaPageFields.find((x) => x.schemaItem === parentId);
        dropDownValue =
          values?.[tableName]?.[tdkeys]?.["ELC" + schema?.fieldName];
      }
      if (dropDownValue) {
        for (const [key, value] of Object.entries(element.lists)) {
          if (parseInt(key) === parseInt(dropDownValue)) {
            setOptions(value);
            break;
          }
        }
      }
    } else if (!element.hasChildList && element.lists) {
      let parentDropDownValue = null;
      
      if (tableELLValue) {
        const ellValueParts = tableELLValue.split("|");
        parentDropDownValue = ellValueParts[1];
      } else {
        if (parentPageId === schemaPageId) {
          // SCENARIO A: Parent is on the MAIN PAGE (e.g., Garment Type)
          const schema = schemaResult?.find((x) => x.schemaItem === parentId);
          if (schema) {
            const rawParentValue = values?.[schema.fieldName]; // e.g., '69461' or 'MAN Shirts'
            
            // 1. Check if Formik already gave us the ID directly
            if (element.lists[rawParentValue]) {
              parentDropDownValue = rawParentValue;
            } 
            // 2. If it's not a direct ID, reverse-lookup the label
            else {
              let opt = schema?.options?.find((x) => x.label === rawParentValue);
              if (opt) {
                parentDropDownValue = opt.value;
              } else if (schema?.lists) {
                for (const listKey in schema.lists) {
                  let lOpt = schema.lists[listKey]?.find((x) => x.label === rawParentValue);
                  if (lOpt) { parentDropDownValue = lOpt.value; break; }
                }
              }
            }
          }
        } else if (parentPageId === itemId) {
          // SCENARIO B: Parent is INSIDE THE TABLE
          const schema = schemaPageFields?.find((x) => x.schemaItem === parentId);
          if (schema) {
            parentDropDownValue = values?.[tableName]?.[tdkeys]?.["ELC" + schema.fieldName];
          }
        }
        // -----------------------------------------------------------------------------
      }

      if (parentDropDownValue && element.lists) {
        for (const [key, value] of Object.entries(element.lists)) {
          if (parseInt(key) === parseInt(parentDropDownValue)) {
            setOptions(value);
            return;
          }
        }
      }
      setOptions([]);
    }
  }, [
    tableELLValue,
    parentValueForDependency,  // Only triggers when the parent changes
    currentValueForDependency  // Only triggers when this specific field changes
  ]);

  const handleChange = (e) => {
    const option = element.hasChildList
      ? options.find((x) => x.label === e.target.value)
      : e.target.value;
    setFieldValue(
      `${tableName}[${tdkeys}].${name}`,
      element.hasChildList ? option?.label : e.target.value
    );
    setFieldValue(
      `${tableName}[${tdkeys}].ELC${name}`,
      element.hasChildList ? option?.value : e.target.value
    );
    handleResetELCList(true);
    const selectedValue = e.target.value;
    
    // Delegate child-list fetch to parent via callback to avoid duplicate calls
    if (element.hasChildList && selectedValue && option?.value && fetchParentELCHeaderData) {
      fetchParentELCHeaderData(
        element,
        tdkeys,
        option?.value,
        ELCDropDown,
        tdkeys
      );
    }
  };

    return (
    <div className="wrapper">
      <div>
        {loadingStates[tdkeys] ? (
          <div className="loading-indicator">Loading...</div>
        ) : (
          <Field
            as="select"
            className={`tableInput ${errorBorder}`}
            id={id + tdkeys + 1}
            rowkeys={tdkeys}
            tdkeys={rowkeys}
            name={`${tableName}[${tdkeys}].${name}`}
            onChange={handleChange}
            value={values?.[tableName]?.[tdkeys]?.[name] || ""}
            data-testid="TableELCSelectField"
          >
            <option value=""></option>

            {(element.hasChildList && ELLValue
              ? options
              : !element.hasChildList && (tableELLValue || options.length > 0)
              ? options
              : ELCDropDown[Object.keys(ELCDropDown)[0]]?.keyValues?.[tdkeys] ||
                []
            ).map((i, index) => (
              <option key={index} value={i.label}>
                {i.label}
              </option>
            ))}
          </Field>
        )}
        {error?.[name] && (
          <span className="icon">
            <Tooltip title={error[name]}>
              <WarningIcon
                sx={{ color: pink[500] }}
                data-testid="warning-icon"
              />
            </Tooltip>
          </span>
        )}
      </div>
    </div>
  );
};

export default TableELCSelectField;
